import { apiClient } from '@/api/client'
import {
  PAYROLL_BATCHES,
  PAYROLL_BATCH_DASHBOARD,
  PAYROLL_BATCH_READINESS,
  PAYROLL_BATCHES_FINALIZE,
  PAYROLL_BATCH_FINALIZE_PREVIEW,
  PAYROLL_BATCH_BULK_APPROVE_PENDING,
  PAYROLL_BATCH_BULK_CALCULATE_DRAFTS,
  PAYROLL_BATCH_BULK_CREATE_RUNS,
  PAYROLL_BATCH_BULK_SUBMIT_CALCULATED,
  payrollBatchPath,
  payrollBatchPreviewPath,
  payrollBatchSummaryPath,
  payrollBatchItemsPath,
  payrollBatchItemPath,
  payrollBatchConsolidatePath,
  payrollBatchApprovePath,
  payrollBatchMarkPaidPath,
  payrollBatchRejectPath,
  payrollBatchResubmitPath,
  payrollBatchVoidPath,
  payrollBatchSlipsPath,
  payrollBatchSlipPdfPath,
  payrollBatchSlipExcelPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  OrgPayrollBatch,
  ConsolidatedPayrollItem,
  ReadinessEntry,
  BatchSummary,
  CreateOrgPayrollBatchDto,
  FinalizeMonthResult,
  BulkPayrollMonthAction,
  BulkPayrollMonthResult,
  PayrollFinalizePreview,
  PayrollMonthDashboard,
} from '@/features/payroll/types/payroll.types'

interface PaginatedData<T> {
  data: T[]
  total: number
  page: number
  limit: number
}

export async function listBatches(
  params?: { page?: number; limit?: number },
): Promise<PaginatedData<OrgPayrollBatch>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedData<OrgPayrollBatch>>>(
    PAYROLL_BATCHES,
    { params },
  )
  return data.data
}

export async function getBatch(batchId: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.get<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchPath(batchId),
  )
  return data.data
}

export async function getReadiness(
  year: number,
  month: number,
): Promise<ReadinessEntry[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ReadinessEntry[]>>(
    PAYROLL_BATCH_READINESS,
    { params: { year, month } },
  )
  return data.data
}

export async function getPayrollDashboard(
  year: number,
  month: number,
): Promise<PayrollMonthDashboard> {
  const { data } = await apiClient.get<ApiResponseWrapper<PayrollMonthDashboard>>(
    PAYROLL_BATCH_DASHBOARD,
    { params: { year, month } },
  )
  return data.data
}

export async function getFinalizePreview(
  year: number,
  month: number,
): Promise<PayrollFinalizePreview> {
  const { data } = await apiClient.get<ApiResponseWrapper<PayrollFinalizePreview>>(
    PAYROLL_BATCH_FINALIZE_PREVIEW,
    { params: { year, month } },
  )
  return data.data
}

export async function createBatch(body: CreateOrgPayrollBatchDto): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    PAYROLL_BATCHES,
    body,
  )
  return data.data
}

export async function finalizeMonth(
  body: CreateOrgPayrollBatchDto,
): Promise<FinalizeMonthResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<FinalizeMonthResult>>(
    PAYROLL_BATCHES_FINALIZE,
    body,
  )
  return data.data
}

export async function bulkPayrollMonthAction(
  action: BulkPayrollMonthAction,
  body: { year: number; month: number },
): Promise<BulkPayrollMonthResult> {
  const endpointByAction: Record<BulkPayrollMonthAction, string> = {
    create_missing: PAYROLL_BATCH_BULK_CREATE_RUNS,
    calculate_drafts: PAYROLL_BATCH_BULK_CALCULATE_DRAFTS,
    submit_calculated: PAYROLL_BATCH_BULK_SUBMIT_CALCULATED,
    approve_pending: PAYROLL_BATCH_BULK_APPROVE_PENDING,
  }
  const { data } = await apiClient.post<ApiResponseWrapper<BulkPayrollMonthResult>>(
    endpointByAction[action],
    body,
  )
  return data.data
}

export async function consolidateBatch(batchId: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchConsolidatePath(batchId),
  )
  return data.data
}

export async function approveBatch(batchId: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchApprovePath(batchId),
  )
  return data.data
}

export async function markPaid(batchId: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchMarkPaidPath(batchId),
  )
  return data.data
}

export async function rejectBatch(batchId: string, reason: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchRejectPath(batchId),
    { reason },
  )
  return data.data
}

export async function resubmitBatch(batchId: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchResubmitPath(batchId),
  )
  return data.data
}

export async function voidBatch(batchId: string): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    payrollBatchVoidPath(batchId),
  )
  return data.data
}

/** Reverses a paid batch: audited, reopens the period. Reason: 10+ characters. */
export async function reverseBatchPayment(
  batchId: string,
  reason: string
): Promise<OrgPayrollBatch> {
  const { data } = await apiClient.post<ApiResponseWrapper<OrgPayrollBatch>>(
    `${payrollBatchPath(batchId)}/reverse-payment`,
    { reason }
  )
  return data.data
}

export async function getBatchSummary(batchId: string): Promise<BatchSummary> {
  const { data } = await apiClient.get<ApiResponseWrapper<BatchSummary>>(
    payrollBatchSummaryPath(batchId),
  )
  return data.data
}

export async function listConsolidatedItems(
  batchId: string,
  params?: { page?: number; limit?: number },
): Promise<PaginatedData<ConsolidatedPayrollItem>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedData<ConsolidatedPayrollItem>>>(
    payrollBatchItemsPath(batchId),
    { params },
  )
  return data.data
}

export async function getConsolidatedItem(
  batchId: string,
  employeeId: string,
): Promise<ConsolidatedPayrollItem> {
  const { data } = await apiClient.get<ApiResponseWrapper<ConsolidatedPayrollItem>>(
    payrollBatchItemPath(batchId, employeeId),
  )
  return data.data
}

export async function downloadBatchPreview(
  batchId: string,
  format: 'pdf' | 'csv',
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    payrollBatchPreviewPath(batchId),
    { params: { format }, responseType: 'blob' },
  )
  return data
}

// ─── Salary slips (admin) ──────────────────────────

export async function downloadBatchSlipPdf(
  batchId: string,
  employeeId: string,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    payrollBatchSlipPdfPath(batchId, employeeId),
    { responseType: 'blob' },
  )
  return data
}

export async function downloadBatchSlipExcel(
  batchId: string,
  employeeId: string,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    payrollBatchSlipExcelPath(batchId, employeeId),
    { responseType: 'blob' },
  )
  return data
}

/** Bulk ZIP of every employee's styled salary slip for the batch. */
export async function downloadBatchSlipsZip(batchId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(payrollBatchSlipsPath(batchId), {
    responseType: 'blob',
  })
  return data
}
